import type { CmsVehicle } from '@/services/cms/models/CmsVehicle';

export const vehicleSortOrders = ['default', 'price-asc', 'price-desc', 'year-asc', 'year-desc', 'odometer-asc', 'odometer-desc'] as const;
export type VehicleSort = (typeof vehicleSortOrders)[number];
export type FilterType = {
    locations: string[];
    priceRange: [number, number];
    berths: number[];
    yearRange: [number, number];
    make: string;
    model: string;
    sortBy: VehicleSort;
};

function getUniqueSorted<T>(arr: T[]): T[] {
    return Array.from(new Set(arr)).sort();
}

export function getAvailableModels(vehicles: CmsVehicle[], make: string) {
    return make ? getUniqueSorted(vehicles.filter((v) => v.make === make).map((v) => v.model)) : [];
}

export function filterVehicles(vehicles: CmsVehicle[], filters: FilterType) {
    return vehicles.filter((v) => {
        if (filters.locations.length > 0 && !filters.locations.includes(v.location.trim())) {
            return false;
        }
        if (filters.priceRange[0] && (v.discountedPrice || v.price) < filters.priceRange[0]) {
            return false;
        }
        if (filters.priceRange[1] && (v.discountedPrice || v.price) > filters.priceRange[1]) {
            return false;
        }
        if (filters.berths.length > 0 && !filters.berths.includes(v.berths)) {
            return false;
        }
        if (v.year < filters.yearRange[0]) {
            return false;
        }
        if (v.year > filters.yearRange[1]) {
            return false;
        }
        if (filters.make && v.make !== filters.make) {
            return false;
        }
        if (filters.model && v.model !== filters.model) {
            return false;
        }
        return true;
    });
}

export function getFilterOptions(vehicles: CmsVehicle[]) {
    const prices = vehicles.map((v) => v.discountedPrice || v.price);
    const minPrice = Math.floor(Math.min(...prices) / 10000) * 10000;
    const maxPrice = Math.ceil(Math.max(...prices) / 10000) * 10000;
    const availableLocations = getUniqueSorted(vehicles.map((v) => v.location.trim()));
    const availableBerths = getUniqueSorted(vehicles.map((v) => v.berths));
    const availableYears = vehicles.map((v) => v.year);
    const minYear = Math.min(...availableYears);
    const maxYear = Math.max(...availableYears);
    const availableMakes = getUniqueSorted(vehicles.map((v) => v.make));
    const region = vehicles[0]?.region || 'NZ';

    const priceStep = 5000;
    const priceOptions: number[] = [];
    for (let p = minPrice; p <= maxPrice; p += priceStep) {
        priceOptions.push(p);
    }
    if (priceOptions[priceOptions.length - 1] !== maxPrice) {
        priceOptions.push(maxPrice);
    }

    const yearOptions: number[] = [];
    for (let y = minYear; y <= maxYear; y++) {
        yearOptions.push(y);
    }

    return {
        minPrice,
        maxPrice,
        availableLocations,
        availableBerths,
        minYear,
        maxYear,
        availableMakes,
        priceOptions,
        yearOptions,
        region,
    };
}

export function sortVehicles(vehicles: CmsVehicle[], sort: VehicleSort) {
    const sorted = [...vehicles];
    switch (sort) {
        case 'price-asc':
            sorted.sort((a, b) => (a.discountedPrice || a.price) - (b.discountedPrice || b.price));
            break;
        case 'price-desc':
            sorted.sort((a, b) => (b.discountedPrice || b.price) - (a.discountedPrice || a.price));
            break;
        case 'year-asc':
            sorted.sort((a, b) => a.year - b.year);
            break;
        case 'year-desc':
            sorted.sort((a, b) => b.year - a.year);
            break;
        case 'odometer-asc':
            sorted.sort((a, b) => (a.odometer || 0) - (b.odometer || 0));
            break;
        case 'odometer-desc':
            sorted.sort((a, b) => (b.odometer || 0) - (a.odometer || 0));
            break;
        default:
            // No sort or default order
            break;
    }
    return sorted;
}
